import React, { useState, useEffect } from "react";
import { BarChart3, Table2, Layers, ShieldCheck, CheckCircle2, PiggyBank,
  CalendarClock, Pencil, Receipt, LogOut } from "lucide-react";
import FilterDropdown from "../components/FilterDropdown";
import { DateField } from "../components/ui/DateField";
import { getDepartmentList } from "../redux/slices/employee";
import { dispatch, useSelector } from "../redux/store";
import { payrollReportsApi, PayrollFilters, PeriodMode } from "../api/salaryReports";
import { getPreviousPayrollPeriod } from "./payrollApprovalLogic";
import { toast } from "sonner";
import { showExtensionMissing } from "../utils/extensionInstall";
// ESI filing is commented out on request; XLSX only ever read its MC template.
// import * as XLSX from "xlsx";
          import { pfSettingsApi } from "../api/pfSettings";
import { sessionGet } from "../utils/sessionStore";
import { pingPfExtension, startFilePf, startPfLogin, onPfStatus, onPfReady, onPfDone, onPfError,
  // getPfDebugLog, getPfExtensionVersion — only the Filing log dialog used these.
  getPfRunState,
  type PfRunMode } from "../utils/pfExtension";
import { inr, Notice } from "./Payroll/reportsUi";
import TdsTab from "./Payroll/TdsTab";
// import ExtensionLogDialog, { type ExtensionLogLine } from "./Payroll/ExtensionLogDialog";
import PfReturnSteps from "./Payroll/PfReturnSteps";
// Only the ESI card built its own step list here; PF has PfReturnSteps.
// import StatutorySteps, { StepNote, STEP_CSS } from "./Payroll/StatutorySteps";
import { pfReturnApi, PfReturnState, PfStepKey } from "../api/pfReturn";
// import { esiApi, EsiBlocker, EsiMonth, ImportedMcRow } from "../api/esi";

// The Teamlio PF extension isn't published to the Chrome Web Store yet
// (dev build, loaded unpacked). TODO: once published, set this to its real
// Web Store URL and re-add the auto-open-store-page fallback below.
import PersonAvatar from "../components/people/PersonAvatar";

type TabId = "overview" | "register" | "components" | "statutory" | "approvals" | "retainer"
  | "leave" | "adjustments" | "reimbursements" | "fullfinal";

const TABS: { id: TabId; name: string; icon: JSX.Element }[] = [
  { id: "overview", name: "Overview", icon: <BarChart3 className="w-4 h-4" /> },
  { id: "register", name: "Salary Register", icon: <Table2 className="w-4 h-4" /> },
  { id: "components", name: "Components", icon: <Layers className="w-4 h-4" /> },
  { id: "statutory", name: "Statutory", icon: <ShieldCheck className="w-4 h-4" /> },
  { id: "approvals", name: "Approvals", icon: <CheckCircle2 className="w-4 h-4" /> },
  { id: "retainer", name: "Retainer", icon: <PiggyBank className="w-4 h-4" /> },
  { id: "leave", name: "Leave Impact", icon: <CalendarClock className="w-4 h-4" /> },
  { id: "adjustments", name: "Adjustments", icon: <Pencil className="w-4 h-4" /> },
  { id: "reimbursements", name: "Reimbursements", icon: <Receipt className="w-4 h-4" /> },
  { id: "fullfinal", name: "Full & Final", icon: <LogOut className="w-4 h-4" /> },
];

const now = new Date();
const defaultPayrollPeriod = getPreviousPayrollPeriod(now);
const fyLabel = (y: number, m: number) => {
  const s = m >= 4 ? y : y - 1;
  return `${s}-${String(s + 1).slice(-2)}`;
};


// Header buttons: a size down from .gt-action-btn's 12px/22px so three fit
// beside the title without wrapping.
const headBtn: React.CSSProperties = { whiteSpace: "nowrap", padding: "9px 16px", fontSize: 13 };

// Face + name on one line. Report rows know the employee by emptID, so the
// photo is found through the directory's employee bridge.
const WHO: React.CSSProperties = { display: "inline-flex", alignItems: "center", gap: 6 };

const HEAD_LABEL: Record<string, string> = {
  basicsalary: "Basic", da: "DA", hra: "HRA", conveyance: "Conv", medicalallowance: "Medical",
  lta: "LTA", mealallowance: "Meal", allowance: "Allow", others: "Others", bonus: "Bonus",
  extrapay: "Extra Pay", retainer_released: "Retainer Rel.",
  pf: "PF", esi: "ESI", tds: "TDS", proftax: "PT", labourwelfare: "LWF",
};

const STATUS_LABEL: Record<string, string> = {
  PENDING: "Pending approval", APPROVED: "Approved", REJECTED: "Rejected", DRAFT: "Draft (not sent)",
};

// The API's own explanation when it gives one (a 403 says why), else a fallback.
const apiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  return typeof detail === "string" && detail.trim() ? detail : fallback;
};

// The money tabs count approved salaries only; say what was left out so a
// month that isn't approved yet doesn't just look empty.
function NotApprovedNote({ info }: { info?: { records: number; net: number } }) {
  if (!info?.records) return null;
  const many = info.records > 1;
  return (
    <p className="gt-page-sub" style={{ fontSize: 12, margin: "6px 0 12px" }}>
      {info.records} salary record{many ? "s" : ""} ({inr(info.net)}) in this period
      {many ? " are" : " is"} not approved yet, so {many ? "they are" : "it is"} not included.
    </p>
  );
}

const DEP_KEYS = (f: PayrollFilters) =>
  [f.mode, f.year, f.month, f.financial_year, f.start_date, f.end_date, f.department];

function useTabData(fetcher: () => Promise<any>, deps: any[]) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    fetcher()
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => {
        if (!alive) return;
        // A failed request is not an empty period — say what went wrong.
        setData(null);
        setError(apiError(e, "Couldn't load this report. Please try again."));
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return { data, loading, error };
}

export default function PayrollReports() {
  // Whole branch, or only the caller's own figures: asked of the API, which
  // applies the same rule to every report, so the controls match the data.
  const [selfOnly, setSelfOnly] = useState<boolean | null>(null);
  const [scopeError, setScopeError] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    payrollReportsApi.scope()
      .then((r) => { if (alive) setSelfOnly(!!r.data?.self_only); })
      .catch((e) => { if (alive) setScopeError(apiError(e, "Couldn't load Payroll Reports. Please try again.")); });
    return () => { alive = false; };
  }, []);
  const canPayrollManage = selfOnly === false;

  const departmentList = useSelector((s: any) => s.employee.departmentList);
  useEffect(() => { if (canPayrollManage) dispatch(getDepartmentList()); }, [canPayrollManage]);

  const [activeTab, setActiveTab] = useState<TabId>("overview");
  const [showFilters, setShowFilters] = useState(true);
  const [filters, setFilters] = useState<PayrollFilters>({
    mode: "month",
    year: Number(defaultPayrollPeriod.year),
    month: Number(defaultPayrollPeriod.month),
    financial_year: fyLabel(
      Number(defaultPayrollPeriod.year),
      Number(defaultPayrollPeriod.month)
    ),
  });

  const setMode = (mode: PeriodMode) => setFilters((f) => ({ ...f, mode }));
  const patch = (p: Partial<PayrollFilters>) => setFilters((f) => ({ ...f, ...p }));

  // A date range needs both ends, in order, before any report can run.
  const rangeProblem = filters.mode !== "range" ? null
    : !filters.start_date || !filters.end_date ? "Pick both a From and a To date to run the report."
    : filters.start_date > filters.end_date ? "The From date must be on or before the To date."
    : null;
  const ready = selfOnly !== null && !rangeProblem;
  const activeName = TABS.find((t) => t.id === activeTab)?.name || "";

  const exportErr = async (e: any, fallback: string) => {
    let msg = fallback;
    const blob = e?.response?.data;
    if (blob instanceof Blob) { try { msg = JSON.parse(await blob.text())?.detail || msg; } catch {} }
    return msg;
  };
  const onExportCsv = async () => {
    try { await payrollReportsApi.exportCsv(activeTab, filters); toast.success(`${activeName} CSV downloaded`); }
    catch (e: any) { toast.error(await exportErr(e, "CSV export failed")); }
  };
  const onExportPdf = async () => {
    try { await payrollReportsApi.exportPdf(filters); toast.success("PDF downloaded"); }
    catch (e: any) { toast.error(await exportErr(e, "PDF export failed")); }
  };

  // Which run the extension is in the middle of, or null. One flag rather
  // than one per button: the extension drives a single portal tab, so two
  // runs can never be in flight at once, and two booleans could only ever
  // disagree with each other about that.
  const [pfBusy, setPfBusy] = useState<PfRunMode | null>(null);
  // const [debugLines, setDebugLines] = useState<ExtensionLogLine[] | null>(null);
  // Bumped whenever a portal run ends. The stepper watches it and refetches:
  // the run is what changed the facts /pf-return derives from, so that is
  // exactly when the steps can have moved.
  const [pfRunSeq, setPfRunSeq] = useState(0);
  // The extension's running commentary used to exist only as toasts, which
  // are gone in four seconds. That made a portal run unobservable: when it
  // stopped somewhere unexpected there was nothing left on screen saying
  // where, and the answer had to come out of the Filing log every time.
  // The last line stays put until the next run says something else.
  const [portalSays, setPortalSays] = useState<PortalMessage>(null);
  useEffect(() => {
    const endRun = () => { setPfBusy(null); setPfRunSeq((n) => n + 1); };
    const note = (tone: PortalTone, text: string) =>
      setPortalSays({ tone, text, at: new Date() });
    const offStatus = onPfStatus((_step, message) => { note("working", message); toast.info(message); });
    const offReady = onPfReady((message) => { endRun(); note("ok", message); toast.success(message); });
    const offDone = onPfDone((ok) => {
      endRun();
      if (ok) { note("ok", "Portal step finished."); toast.success("Portal step finished."); }
    });
    const offError = onPfError((error) => { endRun(); note("bad", error); toast.error(`PF: ${error}`); });
    return () => { offStatus(); offReady(); offDone(); offError(); };
  }, []);

  // The buttons unlock themselves. Closing the portal window mid-step does
  // send a pfError, but that message can be missed — the service worker was
  // asleep and its saved run had been cleared, this tab was reloaded while
  // the run was going, the message raced the window closing — and a missed
  // one used to leave every step button disabled until the page was
  // reloaded. So while a run is believed to be going, ask the extension
  // whether one actually is, and believe the answer.
  useEffect(() => {
    if (!pfBusy) return;
    let alive = true;
    const id = setInterval(async () => {
      const state = await getPfRunState();
      if (!alive || !state || state.running) return;
      setPfBusy(null);
      setPfRunSeq((n) => n + 1);
    }, 4000);
    return () => { alive = false; clearInterval(id); };
  }, [pfBusy]);

  // // A file the ESI step hands to the extension for this run only. Kept in
  // // page state on purpose: the user asked for the import to upload the file
  // // and nothing else — no employee records touched, nothing stored server
  // // side. It lives until the page is left.
  // const [esiUploadFile, setEsiUploadFile] = useState<EsiUploadFile>(null);

  const startPfRun = async (mode: PfRunMode, upload?: { fileName: string; dataBase64: string }) => {
    // Extension not responding to ping. What that means depends on the build:
    // during dev the unpacked copy almost always just needs a reload in
    // chrome://extensions, and sending that person to the Web Store installs a
    // SECOND copy of the same extension — two of them then answer this ping.
    // On the live site the person has no extension at all, and the old message
    // told them to reload something they did not have, with the install link
    // sitting in the toast as text they could not click.
    // showExtensionMissing() picks the right one, and on a production build
    // offers the listing as a button as well -- the automatic tab is asked
    // for ~10s after the click (this ping) and the popup blocker eats it.
    if (!(await pingPfExtension())) {
      showExtensionMissing();
      return;
    }
    setPfBusy(mode);
    try {
      // // Different portal, different login. An ESI run opened with the EPFO
      // // credentials would just fail on the ESIC form, with nothing saying why.
      // const isEsi = mode.startsWith("esi");
      // ESI filing is commented out on request. 
      // const { data } = isEsi ? await esiApi.getCredentials()
      const { data } = await pfSettingsApi.getCredentials();
      const common = {
        username: data.username,
        password: data.password,
        establishmentId: (data as any).establishment_id || undefined,
        authToken: sessionGet('accessToken') || undefined,
        apiBaseUrl: process.env.REACT_APP_API_BASE_URL,
      };
      // The wage month only goes along on a filing run: a login run has
      // nothing to do with one, and sending it would put a month into the
      // log that no part of that run ever acts on.
      if (mode === "login") await startPfLogin(common);
      else await startFilePf({ ...common, mode, upload, fields: { year: filters.year, month: filters.month } });
    } catch (e: any) {
      setPfBusy(null);
      const detail = e?.response?.data?.detail;
      toast.error(
        e?.response?.status === 404
          ? `Set the ${mode.startsWith("esi") ? "ESIC" : "EPFO"} login first under Settings → Organization → PF/ESIC Settings.`
          : detail || e?.message || (mode === "login" ? "Could not open the EPFO portal" : "Could not start PF filing")
      );
    }
  };


  // What the extension logged on its last run through the EPFO portal. The
  // portal tab's console is the only other copy and it is a tab the extension
  // opened, so this is how a filing that went sideways gets explained.
  // const onShowPfLog = async () => {
    // try {
      // setDebugLines(await getPfDebugLog());
    // } catch (e: any) {
      // if (e?.message === "extension_timeout") {
        // toast.error("Teamlio PF extension not responding — reload it in chrome://extensions, then hard-refresh this tab.");
        // return;
      // }
      // // Something answered but has no debug log: almost always a Web Store
      // // copy running while the edits go into the local unpacked folder, so
      // // name the version that answered rather than saying "failed".
      // const version = await getPfExtensionVersion();
      // toast.error(
        // `Extension v${version || "?"} answered but has no debug log. In chrome://extensions, ` +
        // 'check that "Teamlio PF Filing" is Loaded unpacked from the server/teamlio extension folder.'
      // );
    // }
  // };

  return (
    <div className="gt-themed gt-shell min-h-screen p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h1 className="gt-page-title">Payroll Reports</h1>
          {selfOnly !== null && (
            <p className="gt-page-sub">
              {canPayrollManage
                ? "Deep tracking across every salary head, deduction, approval and retainer."
                : "Your salary, deductions, retainer and approvals."}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <button onClick={onExportCsv} disabled={!ready} title={`Exports the ${activeName} tab`}
            className="gt-action-btn" style={{ ...headBtn, opacity: ready ? 1 : 0.5 }}>
            Export CSV
          </button>
          <button onClick={onExportPdf} disabled={!ready} title="Printable salary register"
            className="gt-action-btn" style={{ ...headBtn, opacity: ready ? 1 : 0.5 }}>
            Register PDF
          </button>
          {/* The portal buttons used to live here. They moved into Statutory,
              beside the filing each one serves: with EPFO and ESIC both in
              that tab, a page-wide "Login to portal" said nothing about
              which portal it meant. */}
        </div>
      </div>

      {/* Filter bar */}
      <div className="bg-white rounded-xl shadow-md border border-gray-100 mb-4 overflow-hidden">
        <button type="button" onClick={() => setShowFilters((v) => !v)}
          className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50">
          <span className="text-sm font-medium text-gray-800">Filters</span>
          <span className="text-gray-500">{showFilters ? "▲" : "▼"}</span>
        </button>
        {showFilters && (
          <div className="px-4 pb-4 border-t border-gray-100 pt-4">
            <div className="flex gap-2 mb-4">
              {(["month", "fy", "range"] as PeriodMode[]).map((m) => (
                <button key={m} onClick={() => setMode(m)}
                  className="px-3 py-1.5 rounded-lg text-sm"
                  style={{ border: "1px solid var(--gt-border)",
                    background: filters.mode === m ? "var(--gt-primary)" : "transparent",
                    color: filters.mode === m ? "#fff" : "var(--gt-text-secondary)" }}>
                  {m === "month" ? "Monthly" : m === "fy" ? "Financial Year" : "Date Range"}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
              {filters.mode === "month" && (<>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
                  <FilterDropdown value={String(filters.month)} onChange={(v) => patch({ month: Number(v) })} searchable={false} ariaLabel="Month" options={Array.from({ length: 12 }, (_, i) => ({ value: String(i + 1), label: new Date(2000, i, 1).toLocaleString("en", { month: "long" }) }))} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                  <FilterDropdown value={String(filters.year)} onChange={(v) => patch({ year: Number(v) })} searchable={false} ariaLabel="Year" options={[0, 1, 2, 3].map((d) => { const y = now.getFullYear() - d; return { value: String(y), label: String(y) }; })} />
                </div>
              </>)}
              {filters.mode === "fy" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Financial Year</label>
                  <FilterDropdown value={filters.financial_year || ""} onChange={(v) => patch({ financial_year: v })} searchable={false} ariaLabel="Financial Year" options={[0, 1, 2].map((d) => { const lbl = fyLabel(now.getFullYear() - d, now.getMonth() + 1); return { value: lbl, label: lbl }; })} />
                </div>
              )}
              {filters.mode === "range" && (<>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
                  <DateField value={filters.start_date || ""} onChange={(v) => patch({ start_date: v })} placeholder="Start" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
                  <DateField value={filters.end_date || ""} onChange={(v) => patch({ end_date: v })} placeholder="End" />
                </div>
              </>)}
              {canPayrollManage && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                  <FilterDropdown value={filters.department || "all"} onChange={(v) => patch({ department: v === "all" ? undefined : v })} ariaLabel="Department" options={[{ value: "all", label: "All departments" }, ...(departmentList || []).map((d: any) => ({ value: d.name, label: d.name }))]} />
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div style={{ borderBottom: "1px solid var(--gt-border)", marginBottom: 24 }}>
        <nav className="flex gap-1 flex-nowrap overflow-x-auto" style={{ scrollbarWidth: "none" }}>
          {TABS.map((t) => {
            const active = activeTab === t.id;
            return (
              <button key={t.id} onClick={() => setActiveTab(t.id)}
                style={{ padding: "10px 12px", fontSize: 13, fontWeight: 600, display: "flex", gap: 6, alignItems: "center",
                  borderBottom: active ? "2px solid var(--gt-primary)" : "2px solid transparent",
                  color: active ? "var(--gt-primary-deep)" : "var(--gt-text-tertiary)" }}>
                {t.icon}{t.name}
              </button>
            );
          })}
        </nav>
      </div>

      <div>
        {scopeError ? <Notice error>{scopeError}</Notice>
          : selfOnly === null ? <Notice>Loading…</Notice>
          : rangeProblem ? <Notice>{rangeProblem}</Notice>
          : (<>
            {activeTab === "overview" && <OverviewTab filters={filters} />}
            {activeTab === "register" && <RegisterTab filters={filters} />}
            {activeTab === "components" && <ComponentsTab filters={filters} />}
            {activeTab === "statutory" && (
              <StatutoryTab filters={filters} canManage={canPayrollManage}
                startPfRun={startPfRun} pfBusy={pfBusy} runSeq={pfRunSeq}
                // esiUploadFile={esiUploadFile} setEsiUploadFile={setEsiUploadFile}
                portalSays={portalSays} />
            )}
            {activeTab === "approvals" && <ApprovalsTab filters={filters} />}
            {activeTab === "retainer" && <RetainerTab filters={filters} />}
            {activeTab === "leave" && <LeaveImpactTab filters={filters} />}
            {activeTab === "adjustments" && <AdjustmentsTab filters={filters} />}
            {activeTab === "reimbursements" && <ReimbursementsTab filters={filters} />}
            {activeTab === "fullfinal" && <FullFinalTab filters={filters} />}
          </>)}
      </div>

      {/* Removed on request: nothing opens this any more. */}
    </div>
  );
}

// ───────────────────────── Overview ─────────────────────────

function OverviewTab({ filters }: { filters: PayrollFilters }) {
  const { data, loading, error } = useTabData(() => payrollReportsApi.overview(filters), DEP_KEYS(filters));
  const [openDept, setOpenDept] = useState<string | null>(null);
  const [regRows, setRegRows] = useState<any[] | null>(null);
  const [regLoading, setRegLoading] = useState(false);

  // Reset the drill-down whenever the period/department filter changes.
  useEffect(() => { setOpenDept(null); setRegRows(null); }, DEP_KEYS(filters));

  if (loading) return <Notice>Loading…</Notice>;
  if (error) return <Notice error>{error}</Notice>;
  if (!data) return <Notice>No payroll data for this period.</Notice>;
  const t = data.totals;
  const notApproved = t.not_approved_count || 0;
  // Bonus / extra pay on salaries still waiting for approval: in no total yet,
  // but a month whose adjustments aren't approved shouldn't read as a flat ₹0.
  const notApprovedExtra = (t.not_approved_bonus || 0) + (t.not_approved_extrapay || 0);
  const tiles = [
    { label: "Net Payout", value: inr(t.net_payout),
      note: notApproved ? `+ ${inr(t.not_approved_net)} not approved yet` : "" },
    { label: "Headcount Paid", value: t.headcount,
      note: notApproved ? `${notApproved} not approved yet` : "" },
    { label: "Bonus + Extra", value: inr(t.bonus + t.extrapay),
      note: notApprovedExtra ? `+ ${inr(notApprovedExtra)} not approved yet` : "" },
    { label: "Retainer Held", value: inr(t.retainer_withheld), note: "" },
    { label: "Pending Approvals", value: t.pending_approvals, note: "Sent, waiting for the approver" },
  ];
  const byDept = data.charts.by_department || [];
  const max = Math.max(1, ...byDept.map((d: any) => d.net));

  const toggleDept = async (dept: string) => {
    if (openDept === dept) { setOpenDept(null); return; }
    setOpenDept(dept);
    if (regRows === null) {
      setRegLoading(true);
      try { const r = await payrollReportsApi.register(filters); setRegRows(r.data.rows || []); }
      catch { setRegRows([]); }
      finally { setRegLoading(false); }
    }
  };
  // One line per employee: over a financial year or a date range the register
  // holds a row per month, which listed the same person once for every month.
  const empsFor = (dept: string) => {
    const byEmp = new Map<string, any>();
    (regRows || [])
      .filter((r: any) => (r.department || "Unassigned") === dept)
      .forEach((r: any) => {
        const e = byEmp.get(r.emptID) || { emptID: r.emptID, name: r.name, designation: r.designation, net_paid: 0, months: 0 };
        e.net_paid += r.net_paid || 0;
        e.months += 1;
        byEmp.set(r.emptID, e);
      });
    return Array.from(byEmp.values()).sort((a: any, b: any) => b.net_paid - a.net_paid);
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {tiles.map((m) => (
          <div key={m.label} className="gt-card p-5">
            <span className="gt-eyebrow">{m.label}</span>
            <p className="gt-display gt-numerals" style={{ fontSize: 24, fontWeight: 800, marginTop: 6 }}>{m.value}</p>
            {m.note && <p className="gt-page-sub" style={{ fontSize: 11, marginTop: 2 }}>{m.note}</p>}
          </div>
        ))}
      </div>
      <div className="gt-card p-5">
        <span className="gt-eyebrow">Net Payout by Department</span>
        <p className="gt-page-sub" style={{ fontSize: 11, marginTop: 2 }}>Click a department to see who's in it and how much each.</p>
        <div className="mt-4 space-y-2">
          {byDept.length === 0 && <p className="gt-page-sub">No breakdown.</p>}
          {byDept.map((d: any) => {
            const isOpen = openDept === d.department;
            const emps = isOpen ? empsFor(d.department) : [];
            return (
              <div key={d.department}>
                <div onClick={() => toggleDept(d.department)} className="flex items-center gap-3"
                  style={{ cursor: "pointer" }}>
                  <span style={{ width: 140, fontSize: 12, display: "flex", alignItems: "center", gap: 4 }} className="truncate">
                    <span style={{ display: "inline-block", transform: isOpen ? "rotate(90deg)" : "none", transition: "transform .15s", color: "var(--gt-text-tertiary)" }}>▸</span>
                    {d.department}
                  </span>
                  <div style={{ flex: 1, background: "var(--gt-surface-2)", borderRadius: 6, height: 18 }}>
                    <div style={{ width: `${(d.net / max) * 100}%`, background: "var(--gt-primary)", height: 18, borderRadius: 6 }} />
                  </div>
                  <span style={{ width: 110, textAlign: "right", fontSize: 12 }}>{inr(d.net)}</span>
                </div>
                {isOpen && (
                  <div style={{ margin: "6px 0 10px 20px", paddingLeft: 12, borderLeft: "2px solid var(--gt-border)" }}>
                    {regLoading && regRows === null ? (
                      <p className="gt-page-sub" style={{ fontSize: 12 }}>Loading…</p>
                    ) : emps.length === 0 ? (
                      <p className="gt-page-sub" style={{ fontSize: 12 }}>No employees paid in this department.</p>
                    ) : (
                      <table style={{ width: "100%", fontSize: 12 }}>
                        <tbody>
                          {emps.map((e: any) => (
                            <tr key={e.emptID}>
                              <td style={{ padding: "3px 0" }}>
                                <span style={WHO}>
                                  <PersonAvatar employeeId={e.emptID} name={e.name} size={20} />
                                  <span>{e.name} <span className="gt-page-sub">· {e.designation || "—"}{e.months > 1 ? ` · ${e.months} months` : ""}</span></span>
                                </span>
                              </td>
                              <td style={{ padding: "3px 0", textAlign: "right", fontWeight: 600 }}>{inr(e.net_paid)}</td>
                            </tr>
                          ))}
                          <tr style={{ borderTop: "1px solid var(--gt-border)" }}>
                            <td style={{ padding: "3px 0", color: "var(--gt-text-tertiary)" }}>{emps.length} employee{emps.length > 1 ? "s" : ""}</td>
                            <td style={{ padding: "3px 0", textAlign: "right", fontWeight: 800 }}>{inr(d.net)}</td>
                          </tr>
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ───────────────────────── Salary Register ─────────────────────────

function RegisterTab({ filters }: { filters: PayrollFilters }) {
  const { data, loading, error } = useTabData(() => payrollReportsApi.register(filters), DEP_KEYS(filters));
  const [open, setOpen] = useState<string | null>(null);
  const [daily, setDaily] = useState<any>(null);

  if (loading) return <Notice>Loading…</Notice>;
  if (error) return <Notice error>{error}</Notice>;
  if (!data || data.rows.length === 0) return (
    <Notice>
      No approved salaries for this period.
      <NotApprovedNote info={data?.not_approved} />
    </Notice>
  );

  const earn = data.columns.earnings as string[];
  const ded = data.columns.deductions as string[];

  const toggle = async (r: any) => {
    const key = `${r.emptID}-${r.year}-${r.month}`;
    if (open === key) { setOpen(null); return; }
    setOpen(key); setDaily(null);
    try {
      const res = await payrollReportsApi.registerDaily(r.emptID, r.year, r.month);
      setDaily(res.data);
    } catch { setDaily({ attendance: [] }); }
  };

  return (
    <div>
      <NotApprovedNote info={data.not_approved} />
      <div className="gt-card p-0" style={{ overflow: "auto", maxHeight: "calc(100vh - 80px)" }}>
        <table style={{ minWidth: 1100, width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "var(--gt-surface-2)", textAlign: "right" }}>
              <th style={{ textAlign: "left", padding: 8, position: "sticky", left: 0, top: 0, zIndex: 3, background: "var(--gt-surface-2)" }}>Employee</th>
              {earn.map((h) => <th key={h} style={{ padding: 8, position: "sticky", top: 0, zIndex: 2, background: "var(--gt-surface-2)" }}>{HEAD_LABEL[h] || h}</th>)}
              <th style={{ padding: 8, position: "sticky", top: 0, zIndex: 2, background: "var(--gt-surface-2)", fontWeight: 800 }}>Gross</th>
              {ded.map((h) => <th key={h} style={{ padding: 8, position: "sticky", top: 0, zIndex: 2, background: "var(--gt-surface-2)", color: "#b00" }}>{HEAD_LABEL[h] || h}</th>)}
              <th style={{ padding: 8, position: "sticky", top: 0, zIndex: 2, background: "var(--gt-surface-2)" }}>Retainer</th>
              <th style={{ padding: 8, position: "sticky", top: 0, zIndex: 2, background: "var(--gt-surface-2)", fontWeight: 800 }}>Net Paid</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r: any) => {
              const key = `${r.emptID}-${r.year}-${r.month}`;
              return (
                <React.Fragment key={key}>
                  <tr onClick={() => toggle(r)} style={{ cursor: "pointer", textAlign: "right", borderTop: "1px solid var(--gt-border)" }}>
                    <td style={{ textAlign: "left", padding: 8, position: "sticky", left: 0, background: "var(--gt-surface-0)" }}>
                      <span style={WHO}>
                        <PersonAvatar employeeId={r.emptID} name={r.name} size={20} />
                        <span>{r.name} <span className="gt-page-sub">· {r.department}</span></span>
                      </span>
                    </td>
                    {earn.map((h) => <td key={h} style={{ padding: 8 }}>{r[h] ? Number(r[h]).toLocaleString("en-IN") : "—"}</td>)}
                    <td style={{ padding: 8, fontWeight: 600 }}>{Number(r.gross_earnings || 0).toLocaleString("en-IN")}</td>
                    {ded.map((h) => <td key={h} style={{ padding: 8, color: "#b00" }}>{r[h] ? Number(r[h]).toLocaleString("en-IN") : "—"}</td>)}
                    <td style={{ padding: 8 }}>{r.retainer_withheld ? Number(r.retainer_withheld).toLocaleString("en-IN") : "—"}</td>
                    <td style={{ padding: 8, fontWeight: 800 }}>{inr(r.net_paid)}</td>
                  </tr>
                  {open === key && (
                    <tr><td colSpan={earn.length + ded.length + 4} style={{ padding: 12, background: "var(--gt-surface-1)" }}>
                      {!daily ? "Loading daily…" : (daily.attendance?.length ? (
                        <table style={{ width: "100%", fontSize: 11 }}>
                          <thead><tr style={{ textAlign: "left" }}>
                            <th>Date</th><th>Status</th><th>Hours</th><th>Day Salary</th><th>OT</th>
                          </tr></thead>
                          <tbody>
                            {daily.attendance.map((a: any, i: number) => (
                              <tr key={i}><td>{a.date}</td><td>{a.status}</td><td>{a.hours}</td>
                                <td>{inr(a.day_salary)}</td><td>{inr(a.overtime_amount)}</td></tr>
                            ))}
                          </tbody>
                        </table>
                      ) : "No daily breakdown stored for this row.")}
                    </td></tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ borderTop: "2px solid var(--gt-border)", textAlign: "right", fontWeight: 700 }}>
              <td style={{ textAlign: "left", padding: 8 }}>Totals</td>
              {earn.map((h) => <td key={h} style={{ padding: 8 }}>{Number(data.totals[h] || 0).toLocaleString("en-IN")}</td>)}
              <td style={{ padding: 8 }}>{Number(data.totals.gross_earnings || 0).toLocaleString("en-IN")}</td>
              {ded.map((h) => <td key={h} style={{ padding: 8 }}>{Number(data.totals[h] || 0).toLocaleString("en-IN")}</td>)}
              <td style={{ padding: 8 }}>{Number(data.totals.retainer_withheld || 0).toLocaleString("en-IN")}</td>
              <td style={{ padding: 8 }}>{inr(data.totals.net_paid)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

// ───────────────────────── Components ─────────────────────────

function ComponentsTab({ filters }: { filters: PayrollFilters }) {
  const { data, loading, error } = useTabData(() => payrollReportsApi.components(filters), DEP_KEYS(filters));
  if (loading) return <Notice>Loading…</Notice>;
  if (error) return <Notice error>{error}</Notice>;
  if (!data || data.employees.length === 0) return <Notice>No salary components in force in this period.</Notice>;
  return (
    <div className="space-y-4">
      {data.employees.map((e: any) => (
        <div key={e.emptID} className="gt-card p-5">
          <div className="flex justify-between mb-3">
            <span style={WHO}>
              <PersonAvatar employeeId={e.emptID} name={e.name} size={20} />
              <strong>{e.name}</strong>
            </span>
            <span className="gt-page-sub">{e.designation} · {e.department}</span>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", fontSize: 12 }}>
              <thead><tr style={{ textAlign: "left", color: "var(--gt-text-tertiary)" }}>
                <th>FY</th><th>Effective</th><th>In force until</th><th>CTC</th><th>Basic</th><th>HRA</th>
                <th>PF</th><th>TDS</th><th>PF Rate</th><th>Declared</th>
              </tr></thead>
              <tbody>
                {e.history.map((h: any) => (
                  <tr key={h.id} style={{ borderTop: "1px solid var(--gt-border)" }}>
                    <td>{h.financial_year || "—"}</td><td>{h.effective_date || "—"}</td><td>{h.effective_until || "current"}</td>
                    <td>{inr(h.ctc)}</td><td>{inr(h.basicsalary)}</td><td>{inr(h.hra)}</td>
                    <td>{inr(h.pf)}</td><td>{inr(h.tds)}</td>
                    <td>{h.pf_employee_rate ? `${h.pf_employee_rate}%${h.pf_rate_locked ? " 🔒" : ""}` : "—"}</td>
                    <td>{h.declaration_locked ? "Locked" : "Open"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

// ───────────────────────── Statutory ─────────────────────────

// The three statutory filings, as sub-tabs of one screen. They were separate
// places before (TDS had its own top-level tab), which made PF and TDS read
// as unrelated jobs when they are the same monthly compliance round.
type StatutorySub = "pf" | "tds";   // "esi" commented out on request

const STATUTORY_SUBS: { id: StatutorySub; name: string }[] = [
  { id: "pf", name: "PF (EPFO)" },
  // { id: "esi", name: "ESI" },
  { id: "tds", name: "TDS" },
];

// // What an import holds: the file's NAME, for showing, and its ROWS. Not the
// // bytes -- the file that goes to the portal is built by the server from these
// // rows, in the one MC format, so keeping the original bytes around would just
// // invite uploading them by mistake.
// type EsiUploadFile = { fileName: string; rows: ImportedMcRow[] } | null;

type PortalTone = "working" | "ok" | "bad";
type PortalMessage = { tone: PortalTone; text: string; at: Date } | null;

/** The last thing the extension said, kept on screen. */
function PortalSays({ message }: { message: PortalMessage }) {
  const colour = !message ? "var(--gt-text-tertiary)"
    : message.tone === "bad" ? "#b91c1c"
    : message.tone === "ok" ? "#166534"
    : "var(--gt-text-secondary)";
  return (
    <div style={{ marginTop: 14, paddingTop: 10, borderTop: "1px solid var(--gt-border)",
      display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12 }}>
      <p style={{ fontSize: 11.5, margin: 0, color: colour, flex: 1 }}>
        {message
          ? <>
              <span style={{ color: "var(--gt-text-tertiary)" }}>
                {message.at.toLocaleTimeString()} ·{" "}
              </span>
              {message.text}
            </>
          : "The extension has not reported anything yet this session."}
      </p>
    </div>
  );
}

function StatutoryTab(props: {
  filters: PayrollFilters;
  canManage: boolean;
  startPfRun: (mode: PfRunMode, upload?: { fileName: string; dataBase64: string }) => void;
  pfBusy: PfRunMode | null;
  runSeq: number;
  // esiUploadFile: EsiUploadFile;
  // setEsiUploadFile: (f: EsiUploadFile) => void;
  portalSays: PortalMessage;
}) {
  const [sub, setSub] = useState<StatutorySub>("pf");
  return (
    <div className="space-y-4">
      {/* Same underlined strip as the tabs above, a size down: these are a
          level below those, and a row of filled pills under a row of plain
          tabs read as the louder of the two. */}
      <nav style={{ display: "flex", gap: 4, flexWrap: "wrap", borderBottom: "1px solid var(--gt-border)" }}>
        {STATUTORY_SUBS.map((t) => {
          const active = sub === t.id;
          return (
            <button key={t.id} onClick={() => setSub(t.id)}
              style={{ padding: "8px 12px", fontSize: 12.5, fontWeight: 600,
                marginBottom: -1,
                borderBottom: active ? "2px solid var(--gt-primary)" : "2px solid transparent",
                color: active ? "var(--gt-primary-deep)" : "var(--gt-text-tertiary)" }}>
              {t.name}
            </button>
          );
        })}
      </nav>
      {sub === "pf" && <PfStatutory {...props} />}
      {/* ESI filing is commented out on request.
      {sub === "esi" && (
        <EsiStatutory filters={props.filters} canManage={props.canManage}
          startPfRun={props.startPfRun} pfBusy={props.pfBusy} runSeq={props.runSeq}
          uploadFile={props.esiUploadFile} setUploadFile={props.setEsiUploadFile}
          portalSays={props.portalSays} />
      )}
      */}
      {/* Lifted unchanged from its old top-level tab -- same component, same
          props, just somewhere that makes sense next to PF. */}
      {sub === "tds" && <TdsTab filters={props.filters} />}
    </div>
  );
}

// const ESI_BLOCKER_TEXT: Record<EsiBlocker, string> = {
  // salary_not_approved: "approve the month's salaries first",
  // ip_number_missing: "some employees have no ESIC IP number",
  // no_members: "nobody to file for this month",
// };

// // ESIC's Monthly Contribution. The file itself is built by hrms_prod from the
// // same approved payroll the EPFO return uses, so the two filings can never
// // disagree about who was paid what.
// // Whitespace is where these headings differ harmlessly: the template has
// // newlines and leading spaces inside them ("IP Number \n(10 Digits)"), and a
// // copy that has been through Excel a few times may not. Collapse it all away
// // and compare what the words actually say.
// function normaliseHeading(value: unknown): string {
  // return String(value ?? "").replace(/\s+/g, " ").trim().toLowerCase();
// }

// function firstSheet(dataUrl: string) {
  // const base64 = dataUrl.slice(dataUrl.indexOf(",") + 1);
  // const book = XLSX.read(base64, { type: "base64" });
  // const name = book.SheetNames[0];
  // return name ? book.Sheets[name] : null;
// }

// /** null when the file is ESIC's MC format; otherwise what is wrong with it. */
// function checkMcFormat(dataUrl: string, expected: string[]): string | null {
  // let rows: any[][];
  // try {
    // const sheet = firstSheet(dataUrl);
    // if (!sheet) return "That workbook has no sheets in it.";
    // rows = XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: false }) as any[][];
  // } catch {
    // return "Could not read that workbook — is it really an Excel file?";
  // }
  // if (!rows.length) return "That sheet is empty — it needs ESIC's header row.";

  // const header = (rows[0] || []).map(normaliseHeading);
  // const want = expected.map(normaliseHeading);

  // if (header.length < want.length) {
    // return `The first sheet has ${header.length} columns; ESIC's template has ${want.length}.`;
  // }
  // for (let i = 0; i < want.length; i += 1) {
    // if (header[i] !== want[i]) {
      // // Named by position AND by text, because "column 3 is wrong" on its own
      // // sends people hunting.
      // return `Column ${i + 1} should be "${expected[i].replace(/\s+/g, " ").trim()}" ` +
        // `but this file has "${String(rows[0][i] ?? "").replace(/\s+/g, " ").trim() || "(blank)"}". ` +
        // `Use ESIC's MC template, or download it from step 1.`;
    // }
  // }
  // return null;
// }

// /** The six MC columns of every data row, exactly as the cells hold them.
 // *
 // *  Deliberately NOT tidied here: the rounding, the name cleaning and the
 // *  reason-code rules live in hrms_prod/utils/esi.py, and the generated file
 // *  already goes through them. Sending the cells as they are means an import
 // *  and Teamlio's own data reach the portal through the same code instead of
 // *  through two sets of rules that can disagree. */
// function mcRowsFrom(dataUrl: string): ImportedMcRow[] {
  // let rows: any[][];
  // try {
    // const sheet = firstSheet(dataUrl);
    // if (!sheet) return [];
    // rows = XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: false }) as any[][];
  // } catch {
    // return [];
  // }
  // const cell = (row: any[], i: number) => {
    // const value = row[i];
    // return value === undefined || value === null || value === "" ? null : value;
  // };
  // return rows.slice(1)
    // .filter((row) => (row || []).some((v) => v !== undefined && v !== null && v !== ""))
    // .map((row) => ({
      // ip_number: cell(row, 0),
      // name: cell(row, 1) === null ? null : String(cell(row, 1)),
      // days: cell(row, 2),
      // wages: cell(row, 3),
      // reason_code: cell(row, 4),
      // last_working_day: cell(row, 5) === null ? null : String(cell(row, 5)),
    // }));
// }

// function EsiStatutory({ filters, canManage, startPfRun, pfBusy, runSeq, uploadFile,
                       // setUploadFile, portalSays, onShowLog }: {
  // filters: PayrollFilters;
  // canManage: boolean;
  // startPfRun: (mode: PfRunMode, upload?: { fileName: string; dataBase64: string }) => void;
  // pfBusy: PfRunMode | null;
  // runSeq: number;
  // uploadFile: EsiUploadFile;
  // setUploadFile: (f: EsiUploadFile) => void;
  // portalSays: PortalMessage;
  // onShowLog: () => void;
// }) {
  // const monthMode = filters.mode === "month" && !!filters.year && !!filters.month;
  // const [data, setData] = useState<EsiMonth | null>(null);
  // const [error, setError] = useState<string | null>(null);
  // const [downloading, setDownloading] = useState(false);
  // const [sending, setSending] = useState(false);
  // const [converting, setConverting] = useState(false);

  // useEffect(() => {
    // if (!monthMode || !canManage) { setData(null); return; }
    // let alive = true;
    // esiApi.get(filters.year as number, filters.month as number)
      // .then((r) => { if (alive) { setData(r.data); setError(null); } })
      // .catch((e) => { if (alive) { setData(null); setError(apiError(e, "Couldn't load the ESIC return.")); } });
    // return () => { alive = false; };
  // }, [monthMode, canManage, filters.year, filters.month, runSeq]);

  // if (!canManage) return <Notice>You don't have permission to view ESI filings.</Notice>;
  // if (!monthMode) {
    // // ESIC files per wage month, same as EPFO — a financial year or a date
    // // range has no single month to file.
    // return <Notice>Pick a single month to see its ESIC contribution file.</Notice>;
  // }
  // if (error) return <Notice error>{error}</Notice>;
  // if (!data) return <Notice>Loading…</Notice>;

  // const onDownload = async () => {
    // setDownloading(true);
    // try {
      // await esiApi.downloadMcFile(filters.year as number, filters.month as number, data.filename);
      // toast.success(`${data.filename} downloaded`);
    // } catch (e: any) {
      // let msg = "Could not build the ESIC file";
      // const blob = e?.response?.data;
      // if (blob instanceof Blob) { try { msg = JSON.parse(await blob.text())?.detail || msg; } catch {} }
      // toast.error(msg);
    // } finally {
      // setDownloading(false);
    // }
  // };

  // // The import. Nothing is CREATED from this file — no employee, no payroll
  // // row, nothing stored server side. It is read in the browser, checked
  // // against ESIC's template, held for this page, and handed to the extension
  // // to put on the portal. That is the whole job it has.
  // //
  // // The check matters because the portal's failure is unhelpful: a file with
  // // the wrong columns is rejected there, after a login and a walk through the
  // // portal, with no indication of which column was wrong. Catching it here
  // // costs one read and says exactly what is off.
  // const onImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    // const file = event.target.files?.[0];
    // event.target.value = "";          // so picking the same file again re-fires
    // if (!file) return;
    // if (!/\.(xls|xlsx)$/i.test(file.name)) {
      // toast.error("Pick the ESIC MC template (.xls or .xlsx).");
      // return;
    // }
    // const reader = new FileReader();
    // reader.onload = () => {
      // const result = String(reader.result || "");
      // if (!result.slice(result.indexOf(",") + 1)) {
        // toast.error("That file came back empty."); return;
      // }

      // const problem = checkMcFormat(result, data.columns);
      // if (problem) { toast.error(problem); return; }

      // const rows = mcRowsFrom(result);
      // if (!rows.length) { toast.error("That file has the right columns but no member rows."); return; }
      // setUploadFile({ fileName: file.name, rows });
      // toast.success(`${file.name} read — ${rows.length} row${rows.length === 1 ? "" : "s"}`);
    // };
    // reader.onerror = () => toast.error("Could not read that file.");
    // reader.readAsDataURL(file);
  // };

  // const prepared = data.ready && data.members > 0;
  // const loggingIn = pfBusy === "esiLogin";
  // const filing = pfBusy === "esiUpload";
  // const esiBusy = loggingIn || filing;

  // // Which file the portal gets. Two sources, and the answer has to be one of
  // // them rather than "whichever happened to be wired": an imported file is an
  // // explicit instruction, so it WINS over the generated one. With nothing
  // // imported, Teamlio's own file goes up — the download-then-import round
  // // trip would be busywork, since both files are the same format built from
  // // the same headings.
  // // Which DATA the file is built from. The format is Teamlio's either way --
  // // an import supplies rows, not a file to pass through -- so the name shown
  // // is always the one the server writes.
  // const source = uploadFile
    // ? { label: data.filename, from: `${uploadFile.rows.length} imported row${uploadFile.rows.length === 1 ? "" : "s"}` }
    // : prepared ? { label: data.filename, from: "Teamlio's payroll" } : null;

  // // The converted file, on disk. The import is not what gets filed -- its
  // // ROWS are, rewritten in Teamlio's MC format -- so there has to be a way
  // // to look at the result without going through the portal to find it.
  // const onDownloadConverted = async () => {
    // if (!uploadFile) return;
    // setConverting(true);
    // try {
      // const built = await esiApi.downloadMcFileFromImport(
        // filters.year as number, filters.month as number, uploadFile.rows);
      // toast.success(`${built.filename} downloaded \u2014 ${built.members} member${built.members === 1 ? "" : "s"}`);
    // } catch (e: any) {
      // toast.error(apiError(e, "Could not build that file."));
    // } finally {
      // setConverting(false);
    // }
  // };

  // // The file is built at the moment the run starts, never held in page state:
  // // from live payroll it could already be stale, and from an import it is the
  // // server that owns the MC format either way. One builder, two sources.
  // const onFile = async () => {
    // const year = filters.year as number;
    // const month = filters.month as number;
    // setSending(true);
    // try {
      // const { data: built } = uploadFile
        // ? await esiApi.mcFileFromImport(year, month, uploadFile.rows)
        // : await esiApi.mcFileBase64(year, month);
      // startPfRun("esiUpload", { fileName: built.filename, dataBase64: built.dataBase64 });
    // } catch (e: any) {
      // toast.error(apiError(e, "Could not build the ESIC file to upload."));
    // } finally {
      // setSending(false);
    // }
  // };
  // const steps: { key: string; title: string; state: PfStepState; note: React.ReactNode; action: React.ReactNode }[] = [
    // {
      // key: "prepare",
      // title: "Prepare data",
      // state: prepared ? "done" : "blocked",
      // note: prepared
        // ? <StepNote>{data.members} member{data.members === 1 ? "" : "s"} · ₹{data.total_wages.toLocaleString("en-IN")}</StepNote>
        // : <StepNote tone="warn">{data.blockers.map((b) => ESI_BLOCKER_TEXT[b]).join("; ")}
            // {data.missing_ip.length > 0 && <> — {data.missing_ip.slice(0, 3).join(", ")}
              // {data.missing_ip.length > 3 ? `, +${data.missing_ip.length - 3} more` : ""}</>}
          // </StepNote>,
      // action: (
        // <button type="button" className="pf-step-btn" onClick={onDownload} disabled={downloading || data.members === 0}>
          // {downloading ? "Building…" : "Download MC Excel"}
        // </button>
      // ),
    // },
    // {
      // key: "upload",
      // title: "Upload data",
      // state: uploadFile ? "done" : prepared ? "current" : "blocked",
      // note: uploadFile
        // ? <StepNote>{uploadFile.fileName} · {uploadFile.rows.length} row{uploadFile.rows.length === 1 ? "" : "s"} imported</StepNote>
        // : <StepNote>Teamlio's own file, or import one in ESIC's MC format</StepNote>,
      // action: (
        // // Side by side, so the choice between the two files is visible rather
        // // than something to remember.
        // <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
          // <label className="pf-step-btn" style={{ display: "inline-block" }}>
            // {uploadFile ? "Choose another" : "Import Excel"}
            // <input type="file" accept=".xls,.xlsx" onChange={onImport} style={{ display: "none" }} />
          // </label>
          // {uploadFile && (
            // <button type="button" className="pf-step-btn" onClick={onDownloadConverted} disabled={converting}>
              // {converting ? "Building\u2026" : "Download converted"}
            // </button>
          // )}
          // {uploadFile && (
            // <button type="button" className="pf-step-btn" onClick={() => setUploadFile(null)}>
              // Use Teamlio's data
            // </button>
          // )}
        // </div>
      // ),
    // },
    // {
      // key: "file",
      // title: "File",
      // state: source ? "current" : "locked",
      // note: source
        // ? <StepNote>uploads <strong>{source.label}</strong> from {source.from}</StepNote>
        // : <StepNote>no data to file yet</StepNote>,
      // action: (
        // <button type="button" className="pf-step-btn" disabled={esiBusy || sending || !source}
          // onClick={onFile}>
          // {sending ? "Building file…" : filing ? "Filing…" : "File return"}
        // </button>
      // ),
    // },
  // ];

  // return (
    // <div className="space-y-4">
      // <div className="gt-card p-5">
        // <style>{STEP_CSS}</style>
        // <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12 }}>
          // <span className="gt-eyebrow">ESIC monthly contribution</span>
          // <button type="button" className="pf-step-btn" style={{ marginTop: 0 }}
            // disabled={esiBusy}
            // title="Open the ESIC employer portal and sign in with the saved credentials. You solve the CAPTCHA; the extension clears the two notices and stops there."
            // onClick={() => startPfRun("esiLogin")}>
            // {loggingIn ? "Opening portal…" : "Login to portal"}
          // </button>
        // </div>
        // <p className="gt-page-sub" style={{ fontSize: 12, margin: "4px 0 14px" }}>
          // Built from the same approved payroll as the EPFO return, in ESIC's own
          // MC template. The file carries days and wages only — ESIC works out the
          // contributions from them. Import your own file to upload that instead;
          // either way it has to be the same six-column MC format.
        // </p>

        // <StatutorySteps steps={steps} />

        // <NotApprovedNote info={data.not_approved} />
        // <PortalSays message={portalSays} onShowLog={onShowLog} />
      // </div>

      // {data.preview.length > 0 && (
        // <div className="gt-card p-0" style={{ overflow: "auto", maxHeight: "calc(100vh - 80px)" }}>
          // <table style={{ width: "100%", fontSize: 12 }}>
            // <thead><tr style={{ background: "var(--gt-surface-2)", textAlign: "right" }}>
              // {["IP Number", "IP Name", "Days", "Total Monthly Wages", "Reason", "Last Working Day"].map((h, i) => (
                // <th key={h} style={{ textAlign: i < 2 ? "left" : "right", padding: 8,
                  // position: "sticky", top: 0, zIndex: 2, background: "var(--gt-surface-2)", whiteSpace: "nowrap" }}>{h}</th>
              // ))}
            // </tr></thead>
            // <tbody>
              // {data.preview.map((r) => (
                // <tr key={r.emptID} style={{ borderTop: "1px solid var(--gt-border)", textAlign: "right" }}>
                  // <td style={{ textAlign: "left", padding: 8, fontFamily: "var(--gt-font-mono)" }}>{r.ip_number}</td>
                  // <td style={{ textAlign: "left", padding: 8 }}>{r.name}</td>
                  // <td style={{ padding: 8 }}>{r.days}</td>
                  // <td style={{ padding: 8 }}>{r.wages.toLocaleString("en-IN")}</td>
                  // <td style={{ padding: 8 }}>{r.reason_code === "" ? "—" : r.reason_code}</td>
                  // <td style={{ padding: 8 }}>{r.last_working_day || "—"}</td>
                // </tr>
              // ))}
            // </tbody>
          // </table>
        // </div>
      // )}
    // </div>
  // );
// }

function PfStatutory({ filters, canManage, startPfRun, pfBusy, runSeq, portalSays }: {
  filters: PayrollFilters;
  canManage: boolean;
  startPfRun: (mode: PfRunMode) => void;
  pfBusy: PfRunMode | null;
  runSeq: number;
  portalSays: PortalMessage;
}) {
  const { data, loading, error } = useTabData(() => payrollReportsApi.statutory(filters), DEP_KEYS(filters));
  const [downloadingEcr, setDownloadingEcr] = useState(false);

  // The EPFO return is a per-WAGE-MONTH filing, so the cycle only has meaning
  // with a single month picked — a financial year or a date range has no one
  // month to file. Same rule the ECR download already follows below.
  const monthMode = filters.mode === "month" && !!filters.year && !!filters.month;

  const [pfReturn, setPfReturn] = useState<PfReturnState | null>(null);
  const [pfReturnError, setPfReturnError] = useState<string | null>(null);
  useEffect(() => {
    if (!monthMode || !canManage) { setPfReturn(null); return; }
    let alive = true;
    pfReturnApi.get(filters.year as number, filters.month as number)
      .then((r) => { if (alive) { setPfReturn(r.data); setPfReturnError(null); } })
      .catch((e) => { if (alive) { setPfReturn(null); setPfReturnError(apiError(e, "Couldn't load the EPFO return status.")); } });
    return () => { alive = false; };
    // runSeq: a portal run just ended, so the facts behind these steps moved.
  }, [monthMode, canManage, filters.year, filters.month, runSeq]);

  if (loading) return <Notice>Loading…</Notice>;
  if (error) return <Notice error>{error}</Notice>;
  if (!data || data.rows.length === 0) return (
    <Notice>
      No statutory data for this period.
      <NotApprovedNote info={data?.not_approved} />
    </Notice>
  );
  const heads = data.heads as string[];
  const ecrHeads = (data.ecr_heads as string[]) || [];
  const ecrLabels = (data.ecr_labels as Record<string, string>) || {};
  // ECR is a per-wage-month EPFO filing, so it only makes sense in "month"
  // mode with both year and month picked — a financial-year/range total has
  // no single wage month to file.
  const canDownloadEcr = filters.mode === "month" && !!filters.year && !!filters.month;

  const downloadEcrTextFile = async () => {
    if (!filters.year || !filters.month) return;
    setDownloadingEcr(true);
    try {
      const res = await pfSettingsApi.getEcrText(filters.year, filters.month);
      const blob = new Blob([res.data.text], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `ECR_${filters.month}_${filters.year}.txt`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Failed to generate the ECR text file");
    } finally {
      setDownloadingEcr(false);
    }
  };

  const pfActions = {
    onUpload: () => startPfRun("file"),
    onApprove: () => startPfRun("approve"),
    onApproveSubmit: () => startPfRun("approveSubmit"),
    onChallan: () => startPfRun("challan"),
    onLogin: () => startPfRun("login"),
    loggingIn: pfBusy === "login",
    portalSays: <PortalSays message={portalSays} />,
    // Both approve modes belong to the same step, so either one running shows
    // that step as busy.
    busy: (pfBusy === "file" ? "upload"
      : pfBusy === "approve" || pfBusy === "approveSubmit" ? "approve"
      : pfBusy === "challan" ? "challan"
      : null) as PfStepKey | null,
  };

  return (
    <div className="space-y-4">
      {pfReturn && <PfReturnSteps data={pfReturn} actions={pfActions} />}
      {pfReturnError && <Notice error>{pfReturnError}</Notice>}
      <div>
        <p className="gt-page-sub" style={{ fontSize: 12 }}>Deductions from every approved salary in the period, month by month.</p>
        <NotApprovedNote info={data.not_approved} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {heads.map((h) => (
          <div key={h} className="gt-card p-5">
            <span className="gt-eyebrow">{HEAD_LABEL[h] || h}</span>
            <p className="gt-display gt-numerals" style={{ fontSize: 22, fontWeight: 800, marginTop: 6 }}>{inr(data.totals[h])}</p>
          </div>
        ))}
      </div>
      <div className="gt-card p-0" style={{ overflow: "auto", maxHeight: "calc(100vh - 80px)" }}>
        <table style={{ width: "100%", fontSize: 12 }}>
          <thead><tr style={{ background: "var(--gt-surface-2)", textAlign: "right" }}>
            <th style={{ textAlign: "left", padding: 8, position: "sticky", top: 0, zIndex: 2, background: "var(--gt-surface-2)" }}>Employee</th>
            {heads.map((h) => <th key={h} style={{ padding: 8, position: "sticky", top: 0, zIndex: 2, background: "var(--gt-surface-2)" }}>{HEAD_LABEL[h] || h}</th>)}
          </tr></thead>
          <tbody>
            {data.rows.map((r: any) => (
              <tr key={r.emptID} style={{ borderTop: "1px solid var(--gt-border)", textAlign: "right" }}>
                <td style={{ textAlign: "left", padding: 8 }}>
                  <span style={WHO}>
                    <PersonAvatar employeeId={r.emptID} name={r.name} size={20} />
                    <span>{r.name}</span>
                  </span>
                </td>
                {heads.map((h) => <td key={h} style={{ padding: 8 }}>{r[h] ? Number(r[h]).toLocaleString("en-IN") : "—"}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {ecrHeads.length > 0 && (
        <div>
          <div className="flex items-center justify-between gap-3" style={{ marginBottom: 8 }}>
            <p className="gt-page-sub" style={{ fontSize: 12, margin: 0 }}>
              EPFO ECR preview — same UAN/wages/contribution columns as the return file below.
            </p>
            {canDownloadEcr && (
              <button
                type="button"
                onClick={downloadEcrTextFile}
                disabled={downloadingEcr}
                className="gt-action-btn"
                style={{ ...headBtn, opacity: downloadingEcr ? 0.5 : 1 }}
                title="Download the exact text file EPFO's Return Filing upload expects"
              >
                {downloadingEcr ? "Generating…" : "Download ECR Text File"}
              </button>
            )}
          </div>
          <div className="gt-card p-0" style={{ overflow: "auto", maxHeight: "calc(100vh - 80px)" }}>
            <table style={{ width: "100%", fontSize: 12 }}>
              <thead><tr style={{ background: "var(--gt-surface-2)", textAlign: "right" }}>
                <th style={{ textAlign: "left", padding: 8, position: "sticky", top: 0, zIndex: 2, background: "var(--gt-surface-2)" }}>Member Name</th>
                {ecrHeads.map((h) => <th key={h} style={{ padding: 8, position: "sticky", top: 0, zIndex: 2, background: "var(--gt-surface-2)", whiteSpace: "nowrap" }}>{ecrLabels[h] || h}</th>)}
              </tr></thead>
              <tbody>
                {data.rows.map((r: any) => (
                  <tr key={r.emptID} style={{ borderTop: "1px solid var(--gt-border)", textAlign: "right" }}>
                    <td style={{ textAlign: "left", padding: 8 }}>{r.name}</td>
                    {ecrHeads.map((h) => (
                      <td key={h} style={{ padding: 8 }}>
                        {h === "uan" ? (r.uan || "—") : (r[h] || r[h] === 0 ? Number(r[h]).toLocaleString("en-IN") : "—")}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ───────────────────────── Approvals ─────────────────────────

function ApprovalsTab({ filters }: { filters: PayrollFilters }) {
  const { data, loading, error } = useTabData(() => payrollReportsApi.approvals(filters), DEP_KEYS(filters));
  if (loading) return <Notice>Loading…</Notice>;
  if (error) return <Notice error>{error}</Notice>;
  if (!data) return <Notice>No approval data.</Notice>;
  const pill = (s: string) => (({
    PENDING: { background: "#fef3c7", color: "#92400e" },
    APPROVED: { background: "#dcfce7", color: "#166534" },
    REJECTED: { background: "#fee2e2", color: "#991b1b" },
    DRAFT: { background: "var(--gt-surface-2)", color: "var(--gt-text-secondary)" },
  } as any)[s] || {});
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {(["PENDING", "APPROVED", "REJECTED", "DRAFT"]).map((k) => (
          <div key={k} className="gt-card p-5">
            <span className="gt-eyebrow">{STATUS_LABEL[k]}</span>
            <p className="gt-display gt-numerals" style={{ fontSize: 24, fontWeight: 800, marginTop: 6 }}>{data.counts[k] || 0}</p>
          </div>
        ))}
      </div>
      <div className="gt-card p-0" style={{ overflow: "auto", maxHeight: "calc(100vh - 80px)" }}>
        <table style={{ width: "100%", fontSize: 12 }}>
          <thead><tr style={{ background: "var(--gt-surface-2)", textAlign: "left" }}>
            <th style={{ padding: 8, position: "sticky", top: 0, zIndex: 2, background: "var(--gt-surface-2)" }}>Employee</th><th style={{ padding: 8, position: "sticky", top: 0, zIndex: 2, background: "var(--gt-surface-2)" }}>Period</th>
            <th style={{ padding: 8, position: "sticky", top: 0, zIndex: 2, background: "var(--gt-surface-2)" }}>Status</th><th style={{ padding: 8, position: "sticky", top: 0, zIndex: 2, background: "var(--gt-surface-2)" }}>Approved By</th><th style={{ padding: 8, position: "sticky", top: 0, zIndex: 2, background: "var(--gt-surface-2)" }}>At</th>
          </tr></thead>
          <tbody>
            {data.rows.map((r: any, i: number) => (
              <tr key={i} style={{ borderTop: "1px solid var(--gt-border)" }}>
                <td style={{ padding: 8 }}>
                  <span style={WHO}>
                    <PersonAvatar employeeId={r.emptID} name={r.name} size={20} />
                    <span>{r.name}</span>
                  </span>
                </td>
                <td style={{ padding: 8 }}>{r.month}/{r.year}</td>
                <td style={{ padding: 8 }}><span style={{ ...pill(r.status), padding: "2px 8px", borderRadius: 12, fontSize: 11 }}>{STATUS_LABEL[r.status] || r.status}</span></td>
                <td style={{ padding: 8 }}>
                  {r.approved_by_name ? (
                    <span style={WHO}>
                      {/* The approver is a User, and approved_by is that same
                          User.id the name was looked up from — so the id and
                          name are one person. Never the row's subject. */}
                      <PersonAvatar userId={r.approved_by} name={r.approved_by_name} size={18} />
                      <span>
                        {r.approved_by_name}
                        {r.approved_by_role ? (
                          <span style={{ color: "var(--gt-text-tertiary)", marginLeft: 6, textTransform: "capitalize" }}>
                            ({r.approved_by_role})
                          </span>
                        ) : null}
                      </span>
                    </span>
                  ) : (
                    "—"
                  )}
                </td>
                <td style={{ padding: 8 }}>{r.approved_at ? new Date(r.approved_at).toLocaleDateString() : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ───────────────────────── Retainer ─────────────────────────

function RetainerTab({ filters }: { filters: PayrollFilters }) {
  const { data, loading, error } = useTabData(() => payrollReportsApi.retainer(filters), DEP_KEYS(filters));
  if (loading) return <Notice>Loading…</Notice>;
  if (error) return <Notice error>{error}</Notice>;
  if (!data || data.employees.length === 0) return <Notice>No retainer configs.</Notice>;
  return (
    <div className="space-y-4">
      {data.employees.map((e: any) => (
        <div key={e.emptID} className="gt-card p-5">
          <div className="flex justify-between mb-2">
            <span style={WHO}>
              <PersonAvatar employeeId={e.emptID} name={e.name} size={20} />
              <strong>{e.name}</strong>
            </span>
            <span>Outstanding pool: <b>{inr(e.outstanding)}</b></span>
          </div>
          <p className="gt-page-sub mb-1">
            ₹{e.config?.amount}/mo · {e.config?.period_months}-month cycle · {e.config?.recurring ? "recurring" : "one-time"} · {e.config?.status}
          </p>
          <p className="gt-page-sub mb-3">
            In this period: withheld {inr(e.period_withheld)} · released {inr(e.period_released)}
          </p>
          {e.ledger.length === 0 ? (
            <p className="gt-page-sub" style={{ fontSize: 12 }}>No retainer activity in this period.</p>
          ) : (
            <table style={{ width: "100%", fontSize: 12 }}>
              <thead><tr style={{ textAlign: "right", color: "var(--gt-text-tertiary)" }}>
                <th style={{ textAlign: "left" }}>Period</th><th>Kind</th><th>Withheld</th><th>Released</th><th>Balance</th>
              </tr></thead>
              <tbody>
                {e.ledger.map((l: any, i: number) => (
                  <tr key={i} style={{ textAlign: "right", borderTop: "1px solid var(--gt-border)" }}>
                    <td style={{ textAlign: "left" }}>{l.month ? `${l.month}/${l.year}` : "exit"}</td>
                    <td>{l.kind}</td><td>{inr(l.withheld)}</td><td>{inr(l.released)}</td><td>{inr(l.balance_after)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ))}
    </div>
  );
}

// ───────────────────────── Leave Impact ─────────────────────────

function LeaveImpactTab({ filters }: { filters: PayrollFilters }) {
  const { data, loading, error } = useTabData(() => payrollReportsApi.leaveImpact(filters), DEP_KEYS(filters));
  if (loading) return <Notice>Loading…</Notice>;
  if (error) return <Notice error>{error}</Notice>;
  if (!data || data.rows.length === 0) return <Notice>No approved leave in this period.</Notice>;
  return (
    <div className="space-y-4">
      {data.rows.map((r: any) => (
        <div key={r.emptID} className="gt-card p-5">
          <div className="flex justify-between mb-2">
            <span style={WHO}>
              <PersonAvatar employeeId={r.emptID} name={r.name} size={20} />
              <strong>{r.name} <span className="gt-page-sub">· {r.department}</span></strong>
            </span>
            <span>Leave in this period: <b>{r.total_days} days</b></span>
          </div>
          <div className="flex flex-wrap gap-2 mb-3">
            {Object.entries(r.by_type || {}).map(([t, d]: any) => (
              <span key={t} style={{ background: "var(--gt-surface-2)", borderRadius: 12, padding: "2px 10px", fontSize: 12 }}>
                {t}: {d}d
              </span>
            ))}
          </div>
          <div className="gt-page-sub mb-3" style={{ fontSize: 12 }}>
            {(r.leaves || []).map((l: any, i: number) => (
              <div key={i}>
                {l.leaveType}: {l.fromDate} → {l.toDate} · {l.totalDays}d
                {l.days_in_period !== l.totalDays ? ` (${l.days_in_period}d in this period)` : ""}
              </div>
            ))}
          </div>
          {r.balances?.length > 0 && (
            <>
              <p className="gt-page-sub" style={{ fontSize: 12 }}>Balances, leave year {data.balance_year}</p>
              <table style={{ width: "100%", fontSize: 12 }}>
                <thead><tr style={{ textAlign: "right", color: "var(--gt-text-tertiary)" }}>
                  <th style={{ textAlign: "left" }}>Leave Type</th><th>Opening</th><th>Used</th><th>Closing</th>
                </tr></thead>
                <tbody>
                  {r.balances.map((b: any, i: number) => (
                    <tr key={i} style={{ textAlign: "right", borderTop: "1px solid var(--gt-border)" }}>
                      <td style={{ textAlign: "left" }}>{b.leave_type}</td><td>{b.opening}</td><td>{b.used}</td><td>{b.closing}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      ))}
    </div>
  );
}

// ───────────────────────── Adjustments Audit ─────────────────────────

function AdjustmentsTab({ filters }: { filters: PayrollFilters }) {
  const { data, loading, error } = useTabData(() => payrollReportsApi.adjustments(filters), DEP_KEYS(filters));
  if (loading) return <Notice>Loading…</Notice>;
  if (error) return <Notice error>{error}</Notice>;
  if (!data || data.rows.length === 0) return <Notice>No manual adjustments in this period.</Notice>;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="gt-card p-5"><span className="gt-eyebrow">Total Bonus</span>
          <p className="gt-display gt-numerals" style={{ fontSize: 22, fontWeight: 800, marginTop: 6 }}>{inr(data.total_bonus)}</p></div>
        <div className="gt-card p-5"><span className="gt-eyebrow">Total Extra Pay</span>
          <p className="gt-display gt-numerals" style={{ fontSize: 22, fontWeight: 800, marginTop: 6 }}>{inr(data.total_extrapay)}</p></div>
      </div>
      <p className="gt-page-sub" style={{ fontSize: 12 }}>
        Totals are what these salaries pay now; each row shows the amounts right after that edit.
      </p>
      <div className="gt-card p-0" style={{ overflow: "auto", maxHeight: "calc(100vh - 80px)" }}>
        <table style={{ width: "100%", fontSize: 12 }}>
          <thead><tr style={{ background: "var(--gt-surface-2)", textAlign: "left" }}>
            <th style={{ padding: 8, position: "sticky", top: 0, zIndex: 2, background: "var(--gt-surface-2)" }}>Employee</th><th style={{ padding: 8, position: "sticky", top: 0, zIndex: 2, background: "var(--gt-surface-2)" }}>Period</th>
            <th style={{ padding: 8, position: "sticky", top: 0, zIndex: 2, background: "var(--gt-surface-2)" }}>Bonus after edit</th><th style={{ padding: 8, position: "sticky", top: 0, zIndex: 2, background: "var(--gt-surface-2)" }}>Extra pay after edit</th>
            <th style={{ padding: 8, position: "sticky", top: 0, zIndex: 2, background: "var(--gt-surface-2)" }}>Net after edit</th><th style={{ padding: 8, position: "sticky", top: 0, zIndex: 2, background: "var(--gt-surface-2)" }}>When</th><th style={{ padding: 8, position: "sticky", top: 0, zIndex: 2, background: "var(--gt-surface-2)" }}>Description</th>
          </tr></thead>
          <tbody>
            {data.rows.map((r: any, i: number) => (
              <tr key={i} style={{ borderTop: "1px solid var(--gt-border)" }}>
                <td style={{ padding: 8 }}>
                  <span style={WHO}>
                    <PersonAvatar employeeId={r.emptID} name={r.name} size={18} />
                    <span>{r.name}</span>
                  </span>
                </td>
                <td style={{ padding: 8 }}>{r.month}/{r.year}</td>
                <td style={{ padding: 8 }}>{inr(r.bonus)}</td>
                <td style={{ padding: 8 }}>{inr(r.extrapay)}</td>
                <td style={{ padding: 8 }}>{inr(r.final_net_salary)}</td>
                <td style={{ padding: 8 }}>{r.updated_at ? new Date(r.updated_at).toLocaleDateString() : "—"}</td>
                <td style={{ padding: 8 }}>{r.description || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ───────────────────────── Reimbursements ─────────────────────────

function ReimbursementsTab({ filters }: { filters: PayrollFilters }) {
  const { data, loading, error } = useTabData(() => payrollReportsApi.reimbursements(filters), DEP_KEYS(filters));
  if (loading) return <Notice>Loading…</Notice>;
  if (error) return <Notice error>{error}</Notice>;
  if (!data || data.rows.length === 0) return <Notice>No approved reimbursements in this period.</Notice>;
  return (
    <div className="space-y-4">
      <div className="gt-card p-5"><span className="gt-eyebrow">Total Reimbursed</span>
        <p className="gt-display gt-numerals" style={{ fontSize: 24, fontWeight: 800, marginTop: 6 }}>{inr(data.total_reimbursed)}</p></div>
      <div className="gt-card p-0" style={{ overflow: "auto", maxHeight: "calc(100vh - 80px)" }}>
        <table style={{ width: "100%", fontSize: 12 }}>
          <thead><tr style={{ background: "var(--gt-surface-2)", textAlign: "left" }}>
            <th style={{ padding: 8, position: "sticky", top: 0, zIndex: 2, background: "var(--gt-surface-2)" }}>Employee</th><th style={{ padding: 8, position: "sticky", top: 0, zIndex: 2, background: "var(--gt-surface-2)" }}>Claim</th>
            <th style={{ padding: 8, position: "sticky", top: 0, zIndex: 2, background: "var(--gt-surface-2)" }}>Amount</th><th style={{ padding: 8, position: "sticky", top: 0, zIndex: 2, background: "var(--gt-surface-2)" }}>Status</th><th style={{ padding: 8, position: "sticky", top: 0, zIndex: 2, background: "var(--gt-surface-2)" }}>Submitted</th>
          </tr></thead>
          <tbody>
            {data.rows.map((r: any) => (
              <tr key={r.id} style={{ borderTop: "1px solid var(--gt-border)" }}>
                <td style={{ padding: 8 }}>
                  <span style={WHO}>
                    <PersonAvatar employeeId={r.emptID} name={r.name} size={18} />
                    <span>{r.name}</span>
                  </span>
                </td>
                <td style={{ padding: 8 }}>{r.title || "—"}</td>
                <td style={{ padding: 8 }}>{inr(r.amount)}</td>
                <td style={{ padding: 8 }}>{r.status}</td>
                <td style={{ padding: 8 }}>{r.submitted_on ? new Date(r.submitted_on).toLocaleDateString() : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ───────────────────────── Full & Final ─────────────────────────

function FullFinalTab({ filters }: { filters: PayrollFilters }) {
  const { data, loading, error } = useTabData(() => payrollReportsApi.fullFinal(filters), DEP_KEYS(filters));
  if (loading) return <Notice>Loading…</Notice>;
  if (error) return <Notice error>{error}</Notice>;
  if (!data || data.rows.length === 0) return <Notice>No full &amp; final settlements in this period.</Notice>;
  return (
    <div className="space-y-4">
      <div className="gt-card p-5"><span className="gt-eyebrow">Total Net Payable</span>
        <p className="gt-display gt-numerals" style={{ fontSize: 24, fontWeight: 800, marginTop: 6 }}>{inr(data.total_net_payable)}</p></div>
      {data.rows.map((s: any) => (
        <div key={s.id} className="gt-card p-5">
          <div className="flex justify-between mb-3">
            <span style={WHO}>
              <PersonAvatar employeeId={s.emptID} name={s.name} size={20} />
              <strong>{s.name} <span className="gt-page-sub">· {s.separation_type}</span></strong>
            </span>
            <span style={{ fontSize: 12 }}>{s.status}</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" style={{ fontSize: 12 }}>
            <div><span className="gt-page-sub">Pending Salary</span><div>{inr(s.pending_salary)}</div></div>
            <div><span className="gt-page-sub">Leave Encashment</span><div>{inr(s.leave_encashment)}</div></div>
            <div><span className="gt-page-sub">Gratuity</span><div>{inr(s.gratuity)}</div></div>
            <div><span className="gt-page-sub">Bonus Due</span><div>{inr(s.bonus_due)}</div></div>
            <div><span className="gt-page-sub">Gross Earnings</span><div>{inr(s.gross_earnings)}</div></div>
            <div><span className="gt-page-sub">Total Deductions</span><div style={{ color: "#b00" }}>{inr(s.total_deductions)}</div></div>
            <div><span className="gt-page-sub">Net Payable</span><div style={{ fontWeight: 800 }}>{inr(s.net_payable)}</div></div>
          </div>
        </div>
      ))}
    </div>
  );
}
